import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import View from './view';
import PatientUtils from 'services/PatientUtils';
import { useAppSelector } from 'careand-redux/store';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import { IsPatient } from 'careand-redux/selectors';

const AccountsList = (props) => {
  const { patient = {} } = props;

  const patientId = props.activeId || patient.id;

  const [unlinkConfirmation, setUnlinkConfirmation] = useState(undefined);
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const [isParent, setIsParent] = useState(false);

  const patients = useAppSelector((state) => state.patients.byId);
  const healthRecords = useAppSelector((state) => state.healthRecords.byId);
  const isPatient = useAppSelector(IsPatient);
  const activePatient = patients[patientId];

  const dispatch = useAppDispatch();

  if (!activePatient) return null;

  useEffect(() => {
    if (!isPatient) {
      let accounts = [...(patient.linked_accounts || []), ...(patient.linked_to_accounts || [])];

      let values = [];
      accounts.forEach((linked) => {
        if (patients[linked.id] === undefined) {
          values.push(linked.id);
        }
      });
      if (values.length > 0) {
        dispatch(api.Patients.getMultiplePatients({ multipleValuesPayload: { values } }));
      }
    }
  }, [dispatch, isPatient, patients, patient]);

  useEffect(() => {
    if (isPatient) {
      dispatch(api.Patients.getPatients({}));
    }
  }, [dispatch, isPatient]);

  async function unlink(unlinkPatientId) {
    setUnlinkConfirmation(undefined);

    let parentId = patient.id;
    let linkedId = unlinkPatientId;

    if (isParent) {
      parentId = unlinkPatientId;
      linkedId = patient.id;
    }

    await dispatch(api.Patients.unlinkAccounts({ parentId, linkedId }), {
      successTitle: 'Unlink Account',
      successText: 'Account unlinked successfully!',
    });
  }

  function showConfirmation(patientId, isParent) {
    if (!patientId) {
      setConfirmationMessage('');
      setUnlinkConfirmation(patientId);
      return;
    }

    let unlinkPatient = patients[patientId];
    let message = `Are you sure you want to unlink ${PatientUtils.getName(
      unlinkPatient,
      undefined,
      false,
    )}? `;

    if (isParent) message += "They won't be able to easily access your patient profile anymore.";
    else message += "You won't be able to easily access their patient profile anymore.";

    setIsParent(isParent);
    setConfirmationMessage(message);
    setUnlinkConfirmation(patientId);
  }

  let linkedAccounts = cleanAccounts(patient.linked_accounts, patients, props.activeId);
  let linkedToAccounts = cleanAccounts(patient.linked_to_accounts, patients, props.activeId);

  return (
    <View
      {...props}
      activePatient={activePatient}
      healthRecords={healthRecords}
      patient={patient}
      unlink={unlink}
      confirmationMessage={confirmationMessage}
      linkedAccounts={linkedAccounts}
      linkedToAccounts={linkedToAccounts}
      showConfirmation={showConfirmation}
      unlinkConfirmation={unlinkConfirmation}
      isPatient={isPatient}
    />
  );
};

function cleanAccounts(accounts = [], patients = {}, filterOutId) {
  return accounts
    .map((patient) => patients[patient.id])
    .filter((account) => !!account && account.id !== filterOutId);
}

AccountsList.propTypes = {
  patient: PropTypes.object.isRequired,
  activeId: PropTypes.string,
  sendMessage: PropTypes.func,
  onClick: PropTypes.func,
  sameList: PropTypes.bool,
  highlightSelected: PropTypes.bool,
  allowUnlink: PropTypes.bool,
  showParentAccounts: PropTypes.bool,
};

AccountsList.defaultProps = {
  sameList: false,
  highlightSelected: false,
  allowUnlink: true,
  showParentAccounts: true,
};

export default AccountsList;
